import React, { useEffect, useMemo, useState } from 'react';
import { Route, Routes } from 'react-router';
import { AuthorizationCallback } from './AuthorizationCallback';
import { AuthorizationRequest } from './AuthorizationRequest';
import { useOIDCClient } from './useOIDCClient';
import { OIDCClient } from 'infrastructure/oidc/OIDCClient';
import { SignOutRequest } from './SignOutRequest';
import { useSessionStorageForOIDCTokenSet } from './useSessionStorageForOIDCTokenSet';
import { useOIDCTokenSet } from 'services/useOIDCTokenSet';
import { useSessionRequest, useWithSessionControllerContext, useSessionController, useHookSessionRequest } from './useSessionController';

function useOIDCTokenStorageBinding(oidcClient: OIDCClient) {
    const { tokenSet: storedTokenSet, storeTokenSet } = useSessionStorageForOIDCTokenSet();
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        setLoaded(true);
        if (!storedTokenSet) return;
        oidcClient.setTokenSet(storedTokenSet);
    }, [oidcClient, storedTokenSet]);

    const oidcClientTokenSet = useOIDCTokenSet(oidcClient);
    useEffect(() => {
        if (!loaded) return;
        if (storedTokenSet?.accessToken == oidcClientTokenSet?.accessToken) return;
        storeTokenSet(oidcClientTokenSet);
    }, [loaded, storedTokenSet, oidcClientTokenSet, storeTokenSet]);

    return loaded;
}

function useOIDCClientRevokeDetection(oidcClient: OIDCClient, loaded: boolean) {
    const [revoked, setRevoked] = useState(false);

    useEffect(() => {
        if (!loaded) return;

        const listener = () => {
            if (!oidcClient.tokenSet && !oidcClient.refreshable) {
                setRevoked(true);
            } else if (oidcClient.tokenSet) {
                setRevoked(false);
            }
        };
        listener();
        oidcClient.addTokenSetListener(listener);

        return () => oidcClient.removeTokenSetListener(listener);
    }, [loaded, oidcClient, setRevoked]);

    return revoked;
}

type NonCallbackEndpointProps = {
    oidcClient: OIDCClient;
    flow: string;
    logoutUrl: string;
    onSignOut: () => Promise<void>;
}

function NonCallbackEndpoint(props: React.PropsWithChildren<NonCallbackEndpointProps>) {
    const { oidcClient, onSignOut, flow, logoutUrl, children } = props;

    const sessionController = useSessionController();

    const tokenSetLoaded = useOIDCTokenStorageBinding(oidcClient);
    const tokenSetRevoked = useOIDCClientRevokeDetection(oidcClient, tokenSetLoaded);
    useEffect(() => {
        if (tokenSetRevoked) {
            sessionController.revokeSession();
        }
    }, [sessionController, tokenSetRevoked]);

    const rawRequest = useSessionRequest(oidcClient);
    const request = useHookSessionRequest(rawRequest, 'signout', onSignOut);

    return request === undefined
        ? <></>
        : request === 'stay'
            ? <>{children}</>
            : request === 'signin'
                ? <AuthorizationRequest
                    oidcClient={oidcClient}
                    pkce={flow == 'authorization_code+pkce'} />
                : <SignOutRequest
                    oidcClient={oidcClient}
                    forwardUrl={logoutUrl} />
        ;
}

export type AuthCodeAuthorizationProps = {
    rootPath: string;
    flow: string;
    logoutUrl: string;
    onSignOut: () => Promise<void>;
};

export const AuthCodeAuthorization = (props: React.PropsWithChildren<AuthCodeAuthorizationProps>) => {
    const _oidcClient = useOIDCClient();
    if (!_oidcClient) throw new Error('no OIDC settings given');
    const oidcClient = _oidcClient;

    const redirectPath = useMemo(() => new URL(oidcClient.redirectUrl).pathname, [oidcClient.redirectUrl]);

    const withSessionController = useWithSessionControllerContext();

    return withSessionController(
        <Routes>
            <Route path={redirectPath}
                element={<AuthorizationCallback oidcClient={oidcClient} />}
            />
            <Route path={`${props.rootPath}/*`} element={
                <NonCallbackEndpoint oidcClient={oidcClient} flow={props.flow} logoutUrl={props.logoutUrl} onSignOut={props.onSignOut} >
                    {props.children}
                </NonCallbackEndpoint>
            } />
        </Routes>
    );
};
